import { useNavigate } from '@solidjs/router';
import { useQueryClient } from '@tanstack/solid-query';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createMutation } from '../lib/mutation';
import { orgHref } from '../lib/org';
import { OVERVIEW_COUNTS_QUERY_KEY } from '../lib/overview-counts';
import type { RepoDetailRow } from '../lib/repo-detail';
import { REPOS_QUERY_KEY, setRepoEnabled } from '../lib/repos';
import { canOwnerWrite } from '../lib/roles';
import { useMe } from '../lib/session';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// Repo Settings → enablement for one repo (#972); many at once is `RepoEnablementPanel`. Unlike the read-only override rows next
// to it (#966 removed the fake switches), this control is REAL: `enabled` is a
// genuine DB write plane — `PATCH /v1/repos/:owner/:repo {enabled}` — not a
// fact derived from `.maintainer.yml`. After a successful flip the panel
// navigates back to the repos grid: a disabled repo is hidden from every list
// read (`listEnabled`) and this workspace's next read 404s, so the grid is the
// one surface that can honestly show the outcome (simplest-honest per #972) —
// which it can only do if its cache is staled FIRST: within `staleTime` a fresh
// grid (and Home's repo list and counts) would serve the pre-write rows.

export function RepoEnableSwitchPanel(props: {
  readonly repo: RepoDetailRow;
  readonly token: string | null;
}): JSX.Element {
  const me = useMe(props.token);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // `write:repos` mirror — shut while `/v1/me` is in flight (mid-load `role`
  // is undefined, the same shape as the PAT session that KEEPS the control),
  // and `denied` is not `!allowed`: mid-load the write is unknown, not refused.
  const allowed = (): boolean => !me.isLoading && canOwnerWrite(me.data?.role);
  const denied = (): boolean => !me.isLoading && !canOwnerWrite(me.data?.role);

  const toggle = createMutation({
    call: () => setRepoEnabled(props.repo, !props.repo.enabled, props.token),
    fallback: t('repos.toggle_failed'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [REPOS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [OVERVIEW_COUNTS_QUERY_KEY] });
      navigate(orgHref('/repos'));
    },
  });
  const busy = toggle.pending;
  const error = toggle.error;
  const flip = (): void => void toggle.run();

  return (
    <Panel
      title={t('repo_detail.enablement_title')}
      action={
        <Button variant="secondary" size="sm" disabled={busy() || !allowed()} onClick={flip}>
          {props.repo.enabled ? t('repos.disable') : t('repos.enable')}
        </Button>
      }
    >
      <div class="repo-settings__enablement">
        <StatusPill tone={props.repo.enabled ? 'success' : 'neutral'}>
          {props.repo.enabled ? t('repos.enabled') : t('repos.disabled')}
        </StatusPill>
        <p class="repo-settings__note">{t('repo_detail.enablement_desc')}</p>
      </div>
      <Show when={error()}>
        {(reason) => (
          <p class="repo-settings__note" role="alert">
            {reason()}
          </p>
        )}
      </Show>
      {/* A dead button with no reason reads as a bug; say why it's dead. */}
      <Show when={denied()}>
        <p class="repo-settings__note" role="status">
          {t('repo_detail.enablement_denied')}
        </p>
      </Show>
    </Panel>
  );
}
